import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Users as UsersIcon, UserPlus, Trash2, Edit3, KeyRound, Loader2, ShieldCheck, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { email: "", password: "", name: "", role: "staff" };

function initials(name, email) {
  const src = (name || "").trim() || (email || "").split("@")[0] || "?";
  const parts = src.split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

export default function Staff() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/users");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (u) => {
    setForm({ email: u.email, password: "", name: u.name || "", role: u.role || "staff" });
    setEditing(u);
    setOpen(true);
  };

  const save = async () => {
    if (!form.email.trim() || !form.name.trim()) { toast.error("Name and email required"); return; }
    if (!editing && !form.password) { toast.error("Password required for new staff"); return; }
    setSaving(true);
    try {
      if (editing) {
        const payload = { email: form.email, name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload);
        toast.success(`Updated ${form.name}`);
      } else {
        await api.post("/users", form);
        toast.success(`${form.name} can now log in`);
      }
      setOpen(false); setForm(EMPTY); setEditing(null); await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const del = async (u) => {
    if (u.id === user?.id) { toast.error("You cannot delete your own account"); return; }
    if (!window.confirm(`Delete ${u.name || u.email}? They'll immediately lose access.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Removed");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="h-full overflow-y-auto thin-scroll" data-testid="staff-page">
      <div className="max-w-5xl mx-auto p-4 md:p-8">
        <div className="flex items-start md:items-end justify-between mb-6 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
              <UsersIcon className="w-6 h-6 md:w-7 md:h-7 text-[#D9232E]" /> Staff
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-xl">
              Give every staffer their own login so replies show who answered, and each person tracks their own unread inbox.
            </p>
          </div>
          <Button onClick={openNew} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="new-staff-button">
            <UserPlus className="w-4 h-4 mr-2" /> Add staffer
          </Button>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {loading && <div className="p-8 text-center text-slate-400 text-sm">Loading staff...</div>}
          {!loading && items.length === 0 && (
            <div className="p-8 text-center text-slate-400 text-sm">No staff yet. Add someone to get started.</div>
          )}
          <ul className="divide-y divide-slate-100">
            {items.map((u) => {
              const isMe = u.id === user?.id;
              return (
                <li key={u.id} className="p-4 flex items-center gap-3" data-testid={`staff-row-${u.id}`}>
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-700 shrink-0">
                    {initials(u.name, u.email)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-slate-900 truncate">{u.name || u.email}</div>
                      {u.role === "admin" ? (
                        <Badge className="bg-[#D9232E] text-white hover:bg-[#B91C25] text-[10px] gap-1">
                          <ShieldCheck className="w-3 h-3" /> Admin
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="text-[10px] gap-1">
                          <UserIcon className="w-3 h-3" /> Staff
                        </Badge>
                      )}
                      {isMe && <span className="text-[10px] text-slate-400 uppercase tracking-wide">You</span>}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{u.email}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(u)} title="Edit" data-testid={`edit-staff-${u.id}`}>
                      <Edit3 className="w-4 h-4 text-slate-500" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => del(u)}
                      title="Delete"
                      disabled={isMe}
                      data-testid={`delete-staff-${u.id}`}
                    >
                      <Trash2 className={`w-4 h-4 ${isMe ? "text-slate-300" : "text-slate-500 hover:text-[#D9232E]"}`} />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setEditing(null); } }}>
          <DialogContent data-testid="staff-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? `Edit ${editing.name || editing.email}` : "Add a staffer"}</DialogTitle>
              <DialogDescription>
                {editing ? "Update name, email, role, or reset the password." : "Give them a name, email, and a starter password they can change later."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Full name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Emma Rodriguez" data-testid="staff-name-input" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="emma@danceshowcasevictoria.com" data-testid="staff-email-input" />
              </div>
              <div>
                <Label>{editing ? "New password (leave blank to keep current)" : "Password"}</Label>
                <div className="relative">
                  <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="text"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder={editing ? "Leave blank to keep" : "min 6 chars"}
                    className="pl-9"
                    data-testid="staff-password-input"
                  />
                </div>
              </div>
              <div>
                <Label>Role</Label>
                <div className="flex gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, role: "staff" })}
                    className={`flex-1 border rounded-md p-3 text-sm text-left transition-colors ${form.role === "staff" ? "border-[#D9232E] bg-red-50" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid="staff-role-staff"
                  >
                    <div className="flex items-center gap-2 font-semibold text-slate-900"><UserIcon className="w-4 h-4" /> Staff</div>
                    <div className="text-xs text-slate-500 mt-1">Can text, reply, and see all conversations. Cannot manage staff.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, role: "admin" })}
                    className={`flex-1 border rounded-md p-3 text-sm text-left transition-colors ${form.role === "admin" ? "border-[#D9232E] bg-red-50" : "border-slate-200 hover:border-slate-300"}`}
                    data-testid="staff-role-admin"
                  >
                    <div className="flex items-center gap-2 font-semibold text-slate-900"><ShieldCheck className="w-4 h-4 text-[#D9232E]" /> Admin</div>
                    <div className="text-xs text-slate-500 mt-1">Everything staff can do, plus manage other staff accounts.</div>
                  </button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-[#D9232E] hover:bg-[#B91C25] text-white" data-testid="save-staff-button">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save changes" : "Add staffer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
